import "./HealthCareDevices.scss";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import SearchInput from "../../../components/SearchInput/SearchInput";
import Buttons from "../../../components/Buttons/Buttons";
import Spinner from "../../../components/Spinner/Spinner";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import CustomTooltip from "../../../components/CustomTooltip/CustomTooltip";
import TableStatusRow from "../../../components/TableStatusRow/TableStatusRow";
import { PlusIcon, DeleteIcon, ViewIcon } from "../../../assets/icons-components/Icons";
import useHealthCareDevices from "./useHealthCareDevices";
import HealthCareDeviceFormModal from "./HealthCareDeviceFormModal";

const HEALTHCARE_DEVICES_TABLE_COLUMN_COUNT = 6;

// The backend sends the literal string "Unassigned" for patientName (rather
// than null/empty) when no patient is assigned to a device.
const hasAssignedPatient = (patientName) =>
  Boolean(patientName) && patientName.toLowerCase() !== "unassigned";

export default function HealthCareDevices() {
  const {
    rows,
    page,
    rowsPerPage,
    isLoading,
    isSubmitting,
    deleteDialog,
    addForm,
    errors,
    totalRows,
    fetchError,
    searchQuery,
    modalOpen,
    isViewMode,
    openAddModal,
    openViewModal,
    closeModal,
    handleChange,
    handleSubmit,
    retryFetchDevices,
    handleDelete,
    confirmDelete,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
  } = useHealthCareDevices();

  return (
    <div className="mainadd">
      {isSubmitting && (
        <div className="loader-container">
          <div className="loader">
            <Spinner />
          </div>
        </div>
      )}
      <div className="mainadd_header1">
        <div className="mainadd_header1_left">
          <SearchInput
            className="mainadd_header1_center_searchbar"
            name="searchQuery"
            placeholder="Search by Device Name, ID or Health Professional"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        <div className="mainadd_header1_center">
          <div className="mainadd_header1_center_addbtn">
            <Buttons
              text="Add New Device"
              type="button"
              variant="primary"
              fullWidth={false}
              style={{ height: 40 }}
              onClick={openAddModal}
              icon={<PlusIcon />}
            />
          </div>
        </div>
      </div>
      <div className="mainadd_container">
        <div className="mainadd_container_table">
          <TableContainer component={Paper}>
            <Table aria-label="devices table">
              <TableHead>
                <TableRow>
                  <TableCell>SL NO</TableCell>
                  <TableCell>Device Name</TableCell>
                  <TableCell>Device UDI</TableCell>
                  <TableCell>Number of Slots</TableCell>
                  <TableCell>Assigned Patient</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows && rows.length > 0 ? (
                  rows.map((row, index) => (
                    <TableRow key={row.deviceId}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell style={{ fontWeight: 600, color: "#104581" }}>
                        {row.deviceName || "N/A"}
                      </TableCell>
                      <TableCell>
                        <CustomTooltip title={row.deviceUdi || "-"}>
                          <span className="single-line" style={{ display: "inline-block", maxWidth: "100%" }}>
                            {row.deviceUdi || "-"}
                          </span>
                        </CustomTooltip>
                      </TableCell>
                      <TableCell>{row.numberOfSlots || 0}</TableCell>
                      <TableCell>
                        {hasAssignedPatient(row.patientName) ? (
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontWeight: 600, color: "#1e293b" }}>{row.patientName}</span>
                            <span style={{ fontSize: "12px", color: "#64748b" }}>@{row.patientUserName}</span>
                          </div>
                        ) : (
                          <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Unassigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="table_icons" style={{ display: "flex", gap: "15px" }}>
                          <CustomTooltip title="View">
                            <IconButton aria-label="view" onClick={() => openViewModal(row)}>
                              <ViewIcon />
                            </IconButton>
                          </CustomTooltip>
                          <CustomTooltip title="Delete">
                            <IconButton aria-label="delete" onClick={() => handleDelete(row)}>
                              <DeleteIcon />
                            </IconButton>
                          </CustomTooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableStatusRow
                    colSpan={HEALTHCARE_DEVICES_TABLE_COLUMN_COUNT}
                    loading={isLoading}
                    error={fetchError}
                    onRetry={retryFetchDevices}
                    emptyMessage={
                      searchQuery
                        ? "No devices match your search"
                        : "No devices yet. Click \"Add New Device\" to add one."
                    }
                  />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        <div className="mainadd_container_pagination">
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </div>

      {deleteDialog.open && (
        <ConfirmDialog
          open={deleteDialog.open}
          onClose={deleteDialog.closeDialog}
          title="Delete Device"
          severity="error"
          confirmText="DELETE"
          content={
            hasAssignedPatient(deleteDialog.target?.patientName) ? (
              <span>
                This device is currently assigned to <strong>{deleteDialog.target.patientName}</strong>. Deleting it will unassign it. Are you sure you want to delete this device?
              </span>
            ) : (
              <span>
                Are you sure you want to delete device <strong>{deleteDialog.target?.deviceName || "this Device"}</strong>?
              </span>
            )
          }
          buttonText="Yes, Delete"
          additionalButtonText="Cancel"
          onButtonClick={confirmDelete}
          onAdditionalButtonClick={deleteDialog.closeDialog}
        />
      )}

      <HealthCareDeviceFormModal
        open={modalOpen}
        onClose={closeModal}
        isViewMode={isViewMode}
        addForm={addForm}
        errors={errors}
        onChange={handleChange}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
